import { createReducer, on } from '@ngrx/store';
import { CopyScheduleConflictDto } from '../../api/scheduler/models/copy-schedule-conflict-dto';
import { CrewAvailabilityDto } from '../../api/scheduler/models/crew-availability-dto';
import { MasterScheduleCalendarDto } from '../../api/scheduler/models/master-schedule-calendar-dto';
import { MasterScheduleLegendsDto } from '../../api/scheduler/models/master-schedule-legends-dto';
import { MasterScheduleReferenceDataDto } from '../../api/scheduler/models/master-schedule-reference-data-dto';
import { MasterScheduleLegendMode } from '../../features/master-schedule/models/master-schedule-api.models';
import { MasterScheduleActions } from './master-schedule.actions';

export interface MasterScheduleState {
  calendar: MasterScheduleCalendarDto | null;
  legends: MasterScheduleLegendsDto | null;
  referenceData: MasterScheduleReferenceDataDto | null;
  availableCrew: CrewAvailabilityDto[];
  availableSummary: string;
  copyConflicts: CopyScheduleConflictDto[];
  search: string;
  query: { startDate: string; endDate: string; legendMode: MasterScheduleLegendMode } | null;
  /** Green/grey dots for the selected date range only. Scroll and other loads do not replace this. */
  dotAvailability: Record<string, boolean> | null;
  dotAvailabilityRange: { startDate: string; endDate: string } | null;
  loading: boolean;
  writing: boolean;
  referenceLoading: boolean;
  availableLoading: boolean;
  error: string | null;
}

const initialState: MasterScheduleState = {
  calendar: null,
  legends: null,
  referenceData: null,
  availableCrew: [],
  availableSummary: '',
  copyConflicts: [],
  search: '',
  query: null,
  dotAvailability: null,
  dotAvailabilityRange: null,
  loading: false,
  writing: false,
  referenceLoading: false,
  availableLoading: false,
  error: null,
};

export const masterScheduleReducer = createReducer(
  initialState,
  on(MasterScheduleActions.load, (state, { startDate, endDate, search, legendMode }) => ({
    ...state,
    loading: true,
    error: null,
    search: search ?? state.search,
    query: {
      startDate,
      endDate,
      legendMode: legendMode ?? state.query?.legendMode ?? 'type',
    },
  })),
  on(MasterScheduleActions.loadSuccess, (state, { calendar, legends, search }) => ({
    ...state,
    loading: false,
    writing: false,
    calendar,
    legends,
    search,
    error: null,
    copyConflicts: [],
  })),
  on(MasterScheduleActions.loadFailure, (state, { error }) => ({
    ...state,
    loading: false,
    writing: false,
    error,
  })),
  on(MasterScheduleActions.loadRangeAvailability, (state, { startDate, endDate }) => ({
    ...state,
    dotAvailabilityRange: { startDate, endDate },
  })),
  on(
    MasterScheduleActions.loadRangeAvailabilitySuccess,
    (state, { startDate, endDate, availability }) => {
      const range = state.dotAvailabilityRange;
      if (!range || range.startDate !== startDate || range.endDate !== endDate) {
        return state;
      }
      return {
        ...state,
        dotAvailability: availability,
      };
    }
  ),
  on(MasterScheduleActions.setSearch, (state, { search }) => ({
    ...state,
    search,
  })),
  on(MasterScheduleActions.setLegendMode, (state, { legendMode }) => ({
    ...state,
    query: state.query ? { ...state.query, legendMode } : state.query,
  })),
  on(MasterScheduleActions.loadReferenceData, (state) => ({
    ...state,
    referenceLoading: true,
  })),
  on(MasterScheduleActions.loadReferenceDataSuccess, (state, { data }) => ({
    ...state,
    referenceLoading: false,
    referenceData: data,
  })),
  on(MasterScheduleActions.loadReferenceDataFailure, (state) => ({
    ...state,
    referenceLoading: false,
  })),
  on(MasterScheduleActions.loadAvailableCrew, (state) => ({
    ...state,
    availableLoading: true,
  })),
  on(MasterScheduleActions.loadAvailableCrewSuccess, (state, { items, summaryMessage }) => ({
    ...state,
    availableLoading: false,
    availableCrew: items,
    availableSummary: summaryMessage,
  })),
  on(MasterScheduleActions.loadAvailableCrewFailure, (state) => ({
    ...state,
    availableLoading: false,
    availableCrew: [],
    availableSummary: '',
  })),
  on(
    MasterScheduleActions.assign,
    MasterScheduleActions.replace,
    MasterScheduleActions.deleteSelected,
    MasterScheduleActions.copy,
    (state) => ({
      ...state,
      writing: true,
      error: null,
    })
  ),
  on(
    MasterScheduleActions.assignSuccess,
    MasterScheduleActions.copySuccess,
    (state) => ({
      ...state,
      writing: false,
      copyConflicts: [],
      error: null,
    })
  ),
  on(
    MasterScheduleActions.assignFailure,
    MasterScheduleActions.replaceFailure,
    (state, { error }) => ({
      ...state,
      writing: false,
      error,
    })
  ),
  on(MasterScheduleActions.copyFailure, (state, { error, conflicts }) => ({
    ...state,
    writing: false,
    error,
    copyConflicts: conflicts ?? [],
  })),
  on(MasterScheduleActions.clearConflicts, (state) => ({
    ...state,
    copyConflicts: [],
    error: null,
  }))
);
